import clsx from 'clsx';
import { AlarmClock, AlertTriangle, ArrowLeft, Building2, CalendarClock, ChartColumnBig, Download, FileCode2, FileSpreadsheet, Grid3x3, LayoutGrid, List, ListChecks, Printer, Search, Settings2, Users, Wallet, X } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';
import {
  addDays,
  addMonths,
  buildingView,
  dimValues,
  filterData,
  ganttItems,
  groupBy,
  groupStat,
  matchesSearch,
  matrixModel,
  monthEnd,
  monthStart,
  NONE,
  overdueTasks,
  personMatrix,
  progressOf,
  taskDimValue,
  timeline,
  weekStart,
  zoneView,
  type AnalysisConfig,
  type AnalysisDim,
  type AnalysisOptions,
  type AnalysisSource,
  type AnalysisTask,
  type GroupStat,
} from '@pz/core';
import type { ProcOutput } from '@pz/server';
import { WeekBars } from '../components/charts';
import { BuildingSvg, MiniBuilding, type BuildingMode } from '../components/analysis/building';
import { BurnUp, Gantt, GroupProgress, OverdueList, PersonHours } from '../components/analysis/charts';
import { HoursLegend, pctOr, ProgressLegend, ScheduleBadge, ScheduleLegend, StageBar, StageLegend, Tile, useDims, type AnalysisData } from '../components/analysis/common';
import { MatrixTable } from '../components/analysis/matrix';
import { formaLink } from '../components/entry';
import { Button, Checkbox, EmptyState, ErrorBox, Field, Input, Loading, Modal, Notice, PageHeader, Panel, Select, Tabs, td, th, useToast } from '../components/ui';
import { FieldScopeButton, FieldScopeDialog, OutOfScopeNotice } from '../components/field-scope';
import { saveFile } from '../lib/download';
import { d, h, kw, lang, money, num } from '../lib/format';
import { useM, useQ, useTabParam } from '../lib/hooks';
import { ScrollBox, ScrollTable, Sentinel, useChunked } from '../components/virtual';
import { useSticky } from '../lib/sticky';
import { useMe } from '../lib/me';
import { MoneyLockNotice } from '../components/step-up';
import { HtmlReportHost, ReportHeader, ReportHost } from '../components/report-host';
import { safeName } from '../lib/format';

/**
 * Projektanalyse: the state of the projects for management and project leads, computed from the tasks taken over
 * from Forma (with their custom fields and locations) and the hours booked in Projektzeit.
 * Portfolio: all projects in the scope at a glance. Project: progress by building, floor, trade and work stage,
 * schedule, who works where, hours against plan and (with the rate rights) costs. Filters on every field,
 * export as Excel (data) and as PDF (report via the print dialog).
 */
export function AnalysisPage() {
  const [params, setParams] = useSearchParams();
  const projectId = params.get('project');
  if (projectId) return <ProjectAnalysis projectId={projectId} onBack={() => setParams({}, { replace: false })} />;
  return <Portfolio />;
}

/* ------------------------------------------------------------------ */
/* Period                                                               */
/* ------------------------------------------------------------------ */

type PeriodKey = 'all' | 'year' | 'last12w' | 'last4w' | 'month' | 'lastMonth' | 'week' | 'custom';
const PERIODS: PeriodKey[] = ['all', 'year', 'last12w', 'last4w', 'month', 'lastMonth', 'week', 'custom'];

function periodRange(key: PeriodKey, today: string, custom: { from: string; to: string }): { from: string | null; to: string | null } {
  switch (key) {
    case 'year':
      return { from: `${today.slice(0, 4)}-01-01`, to: today };
    case 'last12w':
      return { from: addDays(weekStart(today), -77), to: today };
    case 'last4w':
      return { from: addDays(weekStart(today), -21), to: today };
    case 'month':
      return { from: monthStart(today), to: today };
    // this week, Monday to today (round 5, request 14)
    case 'week':
      return { from: weekStart(today), to: today };
    case 'lastMonth': {
      const m = monthStart(addMonths(today, -1));
      return { from: m, to: monthEnd(m) };
    }
    case 'custom':
      return { from: custom.from || null, to: custom.to || null };
    default:
      return { from: null, to: null };
  }
}

function usePeriod() {
  const { me } = useMe();
  const [params, setParams] = useSearchParams();
  const key = (PERIODS.includes(params.get('period') as PeriodKey) ? params.get('period') : 'all') as PeriodKey;
  const custom = { from: params.get('from') ?? '', to: params.get('to') ?? '' };
  const range = periodRange(key, me.today, custom);
  const set = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    setParams(next, { replace: true });
  };
  return { key, custom, range, set };
}

function PeriodPicker({ p }: { p: ReturnType<typeof usePeriod> }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-end gap-2">
      <Field label={t('analysis.period')} className="w-48">
        <Select className="h-8" value={p.key} onChange={(e) => p.set({ period: e.target.value === 'all' ? null : e.target.value })}>
          {PERIODS.map((k) => (
            <option key={k} value={k}>
              {t(`analysis.periods.${k}`)}
            </option>
          ))}
        </Select>
      </Field>
      {p.key === 'custom' && (
        <>
          <Field label={t('common.from')}>
            <Input type="date" className="h-8 w-40" value={p.custom.from} onChange={(e) => p.set({ from: e.target.value })} />
          </Field>
          <Field label={t('common.to')}>
            <Input type="date" className="h-8 w-40" value={p.custom.to} onChange={(e) => p.set({ to: e.target.value })} />
          </Field>
        </>
      )}
    </div>
  );
}

function periodText(allLabel: string, range: { from: string | null; to: string | null }) {
  if (!range.from && !range.to) return allLabel;
  return `${range.from ? d(range.from) : ''} - ${range.to ? d(range.to) : ''}`;
}

/** Segmented choice (e.g. colour of the building). */
function Segmented<T extends string>({ value, onChange, items, label }: { value: T; onChange: (v: T) => void; items: { id: T; label: ReactNode }[]; label: string }) {
  return (
    <div role="radiogroup" aria-label={label} className="inline-flex rounded-md border border-line-strong bg-sheet p-0.5">
      {items.map((i) => (
        <button
          key={i.id}
          type="button"
          role="radio"
          aria-checked={value === i.id}
          onClick={() => onChange(i.id)}
          className={clsx('rounded px-2.5 py-1 text-[13px] font-medium whitespace-nowrap', value === i.id ? 'bg-accent text-accent-ink' : 'text-graphite hover:text-ink')}
        >
          {i.label}
        </button>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Portfolio                                                            */
/* ------------------------------------------------------------------ */

const SCHEDULE_ORDER = { late: 0, risk: 1, ok: 2, none: 3 } as const;

function Portfolio() {
  const { t } = useTranslation();
  const toast = useToast();
  const period = usePeriod();
  const [inactive, setInactive] = useState(false);
  const q = useQ('analysis.portfolio', { from: period.range.from, to: period.range.to, includeInactive: inactive || undefined });
  const exportM = useM('analysis.exportPortfolio');
  const [report, setReport] = useState(false);
  const [htmlReport, setHtmlReport] = useState(false);
  const data = q.data;
  const projects = useMemo(
    () => [...(data?.projects ?? [])].sort((a, b) => SCHEDULE_ORDER[a.stat.schedule] - SCHEDULE_ORDER[b.stat.schedule] || a.number.localeCompare(b.number, 'de', { numeric: true })),
    [data],
  );
  // hundreds of projects: the cards are drawn in portions (late projects first) and found by a search
  const [search, setSearch] = useSticky('analysis.search', '');
  const found = useMemo(() => (search.trim() ? projects.filter((p) => matchesSearch(`${p.number} ${p.name} ${p.customerName ?? ''}`, search)) : projects), [projects, search]);
  // many projects (100 and more): a compact list; cards for a handful. The person's choice is kept.
  const [view, setView] = useSticky<'auto' | 'cards' | 'list'>('analysis.view', 'auto');
  const asList = view === 'list' || (view === 'auto' && projects.length > 30);
  const cards = useChunked(found, asList ? 100 : 24, `${search}|${inactive}|${period.range.from}|${period.range.to}|${asList}`);
  const byId = useMemo(() => new Map((data?.projects ?? []).map((p) => [p.id, p])), [data]);
  const overdue = useChunked(data?.overdue ?? [], 50, `${inactive}|${period.range.from}`);
  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={t('analysis.portfolioTitle')}
        subtitle={t('analysis.subtitle')}
        actions={
          data && (
            <>
              <Button
                icon={<FileSpreadsheet className="size-4" />}
                loading={exportM.isPending}
                onClick={() => exportM.mutateAsync({ from: period.range.from, to: period.range.to, includeInactive: inactive || undefined, lang: lang() }).then(saveFile).catch(toast.error)}
              >
                {t('analysis.excel')}
              </Button>
              <Button icon={<Printer className="size-4" />} onClick={() => setReport(true)}>
                {t('analysis.pdf')}
              </Button>
              <Button icon={<FileCode2 className="size-4" />} onClick={() => setHtmlReport(true)} title={t('analysis.export.htmlHint')}>
                {t('analysis.export.html')}
              </Button>
            </>
          )
        }
      />
      <div className="flex flex-wrap items-end gap-4 rounded-lg border border-line bg-sheet p-3">
        <PeriodPicker p={period} />
        <Checkbox className="mb-1.5" label={t('analysis.includeInactive')} checked={inactive} onChange={(e) => setInactive(e.target.checked)} />
        <div className="relative mb-0.5">
          <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-faint" />
          <Input className="w-64 pl-8" type="search" placeholder={t('analysis.search')} aria-label={t('analysis.search')} value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Segmented
          label={t('analysis.view.label')}
          value={asList ? 'list' : 'cards'}
          onChange={(v) => setView(v)}
          items={[
            { id: 'cards', label: <span className="flex items-center gap-1"><LayoutGrid className="size-3.5" />{t('analysis.view.cards')}</span> },
            { id: 'list', label: <span className="flex items-center gap-1"><List className="size-3.5" />{t('analysis.view.list')}</span> },
          ]}
        />
        <span className="mb-1.5 ml-auto text-xs text-graphite">{t('analysis.periodHint')}</span>
      </div>
      {q.isLoading && <Loading />}
      <ErrorBox error={q.error} />
      {data && (
        <>
          <MoneyLockNotice locked={data.money.locked} />
          <PortfolioTiles total={data.total} count={data.projects.length} money={data.money.cost} />
          {projects.length === 0 && <EmptyState>{t('analysis.noProjects')}</EmptyState>}
          {projects.length > 0 && found.length === 0 && <EmptyState>{t('common.noResults')}</EmptyState>}
          {/* many projects (round 5, request 15): the cards scroll in their own area, the page stays short; more load while scrolling */}
          {found.length > 0 && (
            <ScrollBox label={t('analysis.portfolioTitle')} maxHeight="min(72vh, 60rem)" className="pr-1">
              {asList ? (
                <ProjectList projects={cards.visible} money={data.money.cost} />
              ) : (
                <div className="grid gap-3 md:grid-cols-2 2xl:grid-cols-3">
                  {cards.visible.map((p) => (
                    <ProjectCard key={p.id} p={p} money={data.money.cost} />
                  ))}
                </div>
              )}
              {cards.hasMore && <Sentinel onMore={cards.more} />}
            </ScrollBox>
          )}
          {found.length > 0 && (
            <div className="flex items-center justify-center gap-3 text-xs text-graphite">
              <span className="num">{t('analysis.shownOf', { shown: cards.shown, total: cards.total })}</span>
              {cards.hasMore && (
                <Button size="sm" onClick={cards.more}>
                  {t('analysis.more')}
                </Button>
              )}
            </div>
          )}
          <Panel title={<span className="flex items-center gap-2"><AlarmClock className="size-4 text-bad" />{t('analysis.overdueAll')}</span>}>
            {data.overdue.length === 0 ? (
              <p className="text-sm text-graphite">{t('analysis.noOverdue')}</p>
            ) : (
              <ScrollBox label={t('analysis.overdueAll')} maxHeight="min(50vh, 28rem)">
                <ul className="flex flex-col divide-y divide-line/70">
                  {overdue.visible.map((o) => {
                    const p = byId.get(o.projectId);
                    return (
                      <li key={o.id} className="flex items-start gap-2 py-1.5 text-sm">
                        <span className="min-w-0 flex-1">
                          <span className="num font-semibold text-accent">{o.ref}</span> {o.title}
                          <span className="block text-xs text-graphite">
                            {p ? `${p.number} ${p.name}` : ''} · {t('analysis.dueOn', { date: d(o.dueDate) })}
                          </span>
                        </span>
                        <span className="num shrink-0 text-xs font-semibold text-bad">{t('analysis.daysLate', { count: o.days })}</span>
                      </li>
                    );
                  })}
                </ul>
                {overdue.hasMore && <Sentinel onMore={overdue.more} />}
              </ScrollBox>
            )}
          </Panel>
          <p className="text-xs text-graphite">{t('analysis.definitions')}</p>
          <HtmlReportHost open={htmlReport} onDone={() => setHtmlReport(false)} title={t('analysis.portfolioReport')} fileName={`Projektanalyse_Portfolio_${data.today}.html`}>
            <PortfolioReport data={data} projects={projects} periodLabel={periodText(t('analysis.periods.all'), period.range)} />
          </HtmlReportHost>
          <ReportHost open={report} onClose={() => setReport(false)} title={t('analysis.portfolioReport')}>
            <PortfolioReport data={data} projects={projects} periodLabel={periodText(t('analysis.periods.all'), period.range)} />
          </ReportHost>
        </>
      )}
    </div>
  );
}

type PortfolioData = ProcOutput<'analysis.portfolio'>;
type PortfolioProject = PortfolioData['projects'][number];

function PortfolioTiles({ total, count, money: withMoney }: { total: GroupStat; count: number; money: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
      <Tile label={t('analysis.kpi.projects')} value={num(count)} />
      <Tile label={t('analysis.kpi.progress')} value={pctOr(total.completion)} hint={t('analysis.kpi.doneOf', { done: num(total.done), tasks: num(total.tasks) })} />
      <Tile label={t('analysis.kpi.overdue')} value={num(total.overdue)} tone={total.overdue ? 'bad' : undefined} hint={t('analysis.kpi.dueSoon', { count: total.dueSoon })} />
      <Tile label={t('analysis.kpi.actual')} value={`${h(total.actualMinutes, 0)} h`} hint={t('analysis.kpi.lifetime', { hours: h(total.lifetimeMinutes, 0) })} />
      <Tile label={t('analysis.kpi.planRatio')} value={pctOr(total.planRatio)} hint={t('analysis.kpi.planRatioHint')} />
      {withMoney ? (
        <Tile label={t('analysis.kpi.cost')} value={money(total.costCents)} hint={total.costMissingMinutes ? t('analysis.kpi.costMissing', { hours: h(total.costMissingMinutes, 0) }) : t('analysis.kpi.costHint')} />
      ) : (
        <Tile label={t('analysis.kpi.onTime')} value={pctOr(total.onTime)} hint={Number.isFinite(total.avgCloseDays) ? t('analysis.kpi.avgClose', { days: num(total.avgCloseDays, 0) }) : undefined} />
      )}
    </div>
  );
}

/** The portfolio as a table: one row per project, for many projects at a glance. */
function ProjectList({ projects, money: withMoney }: { projects: PortfolioProject[]; money: boolean }) {
  const { t } = useTranslation();
  return (
    <ScrollTable label={t('analysis.view.list')} maxHeight="min(70vh, 48rem)">
      <table className="w-full min-w-[760px] text-sm">
        <thead className="border-b border-line bg-sheet-2">
          <tr>
            <th className={th}>{t('common.project')}</th>
            <th className={th}>{t('analysis.scheduleCol')}</th>
            <th className={clsx(th, 'text-right')}>{t('analysis.kpi.progress')}</th>
            <th className={clsx(th, 'text-right')}>{t('analysis.kpi.tasks')}</th>
            <th className={clsx(th, 'text-right')}>{t('analysis.kpi.overdue')}</th>
            <th className={clsx(th, 'text-right')}>{t('analysis.kpi.actual')}</th>
            <th className={clsx(th, 'text-right')}>{withMoney ? t('analysis.kpi.cost') : t('analysis.kpi.planUse')}</th>
          </tr>
        </thead>
        <tbody>
          {projects.map((p) => (
            <tr key={p.id} className="border-b border-line/70 hover:bg-sheet-2">
              <td className={td}>
                <Link to={`/analysis?project=${p.id}`} className="font-medium text-accent hover:underline">
                  <span className="num">{p.number}</span> {p.name}
                </Link>
                {p.customerName && <div className="text-xs text-graphite">{p.customerName}</div>}
              </td>
              <td className={td}>
                <ScheduleBadge state={p.stat.schedule} />
              </td>
              <td className={clsx(td, 'num text-right')}>{pctOr(p.progress)}</td>
              <td className={clsx(td, 'num text-right')}>
                {num(p.stat.done)} / {num(p.stat.tasks)}
              </td>
              <td className={clsx(td, 'num text-right', p.stat.overdue > 0 && 'font-semibold text-bad')}>{num(p.stat.overdue)}</td>
              <td className={clsx(td, 'num text-right')}>{h(p.stat.actualMinutes, 0)} h</td>
              <td className={clsx(td, 'num text-right')}>{withMoney ? money(p.stat.costCents) : pctOr(p.stat.planUse)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </ScrollTable>
  );
}

function ProjectCard({ p, money: withMoney }: { p: PortfolioProject; money: boolean }) {
  const { t } = useTranslation();
  const s = p.stat;
  return (
    <Link
      to={`/analysis?project=${p.id}`}
      className="flex flex-col gap-3 rounded-lg border border-line bg-sheet p-4 transition-colors hover:border-accent focus-visible:border-accent print-avoid-break"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate font-semibold">
            <span className="num">{p.number}</span> {p.name}
          </div>
          <div className="truncate text-xs text-graphite">{p.customerName ?? ' '}</div>
        </div>
        <ScheduleBadge state={s.schedule} />
      </div>
      <div className="flex items-end gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="num text-2xl font-semibold">{pctOr(p.progress)}</span>
            <span className="text-xs text-graphite">{t('analysis.kpi.doneOf', { done: num(s.done), tasks: num(s.tasks) })}</span>
          </div>
          <StageBar s={s} className="mt-1.5" />
        </div>
        {p.buildings.length > 0 && (
          <div className="shrink-0">
            <MiniBuilding buildings={p.buildings} label={t('analysis.buildingAria')} />
          </div>
        )}
      </div>
      <dl className="grid grid-cols-3 gap-2 text-xs">
        <div>
          <dt className="text-graphite">{t('analysis.kpi.overdue')}</dt>
          <dd className={clsx('num font-semibold', s.overdue > 0 && 'text-bad')}>{num(s.overdue)}</dd>
        </div>
        <div>
          <dt className="text-graphite">{t('analysis.kpi.actual')}</dt>
          <dd className="num font-semibold">{h(s.actualMinutes, 0)} h</dd>
        </div>
        <div>
          <dt className="text-graphite">{withMoney ? t('analysis.kpi.cost') : t('analysis.kpi.planUse')}</dt>
          <dd className="num font-semibold">{withMoney ? money(s.costCents) : pctOr(s.planUse)}</dd>
        </div>
      </dl>
    </Link>
  );
}

function PortfolioReport({ data, projects, periodLabel }: { data: PortfolioData; projects: PortfolioProject[]; periodLabel: string }) {
  const { t } = useTranslation();
  const { me } = useMe();
  return (
    <div className="flex flex-col gap-4 text-ink">
      <ReportHeader title={t('analysis.portfolioReport')} sub={`${t('analysis.period')}: ${periodLabel}`} company={me.settings.companyName} />
      <PortfolioTiles total={data.total} count={data.projects.length} money={data.money.cost} />
      <table className="w-full text-xs">
        <thead className="border-b border-line">
          <tr>
            <th className={th}>{t('common.project')}</th>
            <th className={th}>{t('analysis.kpi.progress')}</th>
            <th className={th}>{t('analysis.schedule.label')}</th>
            <th className={clsx(th, 'text-right')}>{t('analysis.kpi.overdue')}</th>
            <th className={clsx(th, 'text-right')}>{t('analysis.kpi.plan')}</th>
            <th className={clsx(th, 'text-right')}>{t('analysis.kpi.actual')}</th>
            <th className={clsx(th, 'text-right')}>{t('analysis.kpi.planRatio')}</th>
            {data.money.cost && <th className={clsx(th, 'text-right')}>{t('analysis.kpi.cost')}</th>}
            <th className={th}>{t('analysis.buildings')}</th>
          </tr>
        </thead>
        <tbody>
          {projects.map((p) => (
            <tr key={p.id} className="border-b border-line/60 print-avoid-break">
              <td className={td}>
                <div className="font-semibold">
                  {p.number} {p.name}
                </div>
                <div className="text-graphite">{p.customerName}</div>
              </td>
              <td className={clsx(td, 'w-40')}>
                <div className="num font-semibold">
                  {pctOr(p.progress)} <span className="font-normal text-graphite">({num(p.stat.done)}/{num(p.stat.tasks)})</span>
                </div>
                <StageBar s={p.stat} className="mt-1" />
              </td>
              <td className={td}>
                <ScheduleBadge state={p.stat.schedule} />
              </td>
              <td className={clsx(td, 'num text-right')}>{num(p.stat.overdue)}</td>
              <td className={clsx(td, 'num text-right')}>{h(p.stat.plannedMinutes, 0)}</td>
              <td className={clsx(td, 'num text-right')}>{h(p.stat.actualMinutes, 0)}</td>
              <td className={clsx(td, 'num text-right')}>{pctOr(p.stat.planRatio)}</td>
              {data.money.cost && <td className={clsx(td, 'num text-right')}>{money(p.stat.costCents)}</td>}
              <td className={td}>{p.buildings.length > 0 && <MiniBuilding buildings={p.buildings} label={t('analysis.buildingAria')} />}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <ProgressLegend />
      <p className="text-[10px] text-graphite">{t('analysis.definitions')}</p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Project analysis                                                     */
/* ------------------------------------------------------------------ */

type Tab = 'overview' | 'building' | 'matrix' | 'schedule' | 'team' | 'tasks' | 'cost';
const TABS: Tab[] = ['overview', 'building', 'matrix', 'schedule', 'team', 'tasks', 'cost'];

function useFilters() {
  const [params, setParams] = useSearchParams();
  const filters = useMemo(() => {
    const f: Record<string, string> = {};
    for (const [k, v] of params.entries()) if (k.startsWith('f_') && v) f[k.slice(2)] = v;
    return f;
  }, [params]);
  const set = (dim: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(`f_${dim}`, value);
    else next.delete(`f_${dim}`);
    setParams(next, { replace: true });
  };
  const setMany = (patch: Record<string, string>) => {
    const next = new URLSearchParams(params);
    for (const [dim, value] of Object.entries(patch)) {
      if (value) next.set(`f_${dim}`, value);
      else next.delete(`f_${dim}`);
    }
    setParams(next, { replace: true });
  };
  const clear = () => {
    const next = new URLSearchParams(params);
    for (const k of [...next.keys()]) if (k.startsWith('f_')) next.delete(k);
    setParams(next, { replace: true });
  };
  return { filters, set, setMany, clear };
}

/** Analysis of one project (page "Analyse" and the tab of the project page). */
export function ProjectAnalysis({ projectId, onBack, embedded }: { projectId: string; onBack?: () => void; embedded?: boolean }) {
  const { t } = useTranslation();
  const { me } = useMe();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useTabParam<Tab>(TABS, embedded ? 'atab' : 'tab');
  const period = usePeriod();
  const { filters, set, setMany, clear } = useFilters();
  const q = useQ('analysis.project', { projectId });
  const portfolio = useQ('analysis.portfolio', {}, { enabled: !embedded });
  const exportM = useM('analysis.export');
  const [configOpen, setConfigOpen] = useState(false);
  // values of a field the project uses (request 20)
  const [scopeField, setScopeField] = useState<string | null>(null);
  const [report, setReport] = useState(false);
  // export (request 18): which parts and which format; the PDF and the HTML file show the chosen parts of the report
  const [exportOpen, setExportOpen] = useState(false);
  const [parts, setParts] = useState<Set<ReportPart>>(() => new Set(REPORT_PARTS));
  const [htmlReport, setHtmlReport] = useState(false);
  const data = q.data;
  const dims = useDims(data);
  const withMoney = !!data && (data.money.cost || data.money.sales);

  const model = useMemo(() => {
    if (!data) return null;
    const opts: AnalysisOptions = { today: data.today, period: period.range, config: data.config, buildingFloors: data.buildingFloors };
    const f = filterData(data.tasks, data.hours, filters, data.config);
    return { opts, ...f, total: groupStat('all', '', f.tasks, f.hours, opts, withMoney) };
  }, [data, filters, period.range.from, period.range.to, withMoney]); // eslint-disable-line react-hooks/exhaustive-deps

  if (q.isLoading) return <Loading />;
  if (q.error || !data || !model) return <ErrorBox error={q.error} />;
  const active = Object.entries(filters).filter(([, v]) => v);
  const projects = portfolio.data?.projects ?? [];
  const link = (taskId: string) => {
    const task = data.tasks.find((x) => x.id === taskId);
    return task?.formaIssueId ? formaLink(me.settings.deepLinkTemplate, data.project.formaProjectId, task.formaIssueId) : null;
  };

  return (
    <div className="flex flex-col gap-4">
      {!embedded && (
        <PageHeader
          title={
            <span className="flex flex-wrap items-center gap-2">
              {onBack && (
                <button type="button" onClick={onBack} className="rounded p-1 text-graphite hover:bg-sheet-2 hover:text-ink" aria-label={t('analysis.backToPortfolio')} title={t('analysis.backToPortfolio')}>
                  <ArrowLeft className="size-5" />
                </button>
              )}
              {t('analysis.title')}
            </span>
          }
          subtitle={data.project.customerName ?? undefined}
          actions={
            <Select
              className="w-80"
              search
              aria-label={t('common.project')}
              value={projectId}
              onChange={(e) => {
                const next = new URLSearchParams();
                next.set('project', e.target.value);
                setParams(next);
              }}
            >
              {!projects.some((p) => p.id === projectId) && (
                <option value={projectId}>
                  {data.project.number} {data.project.name}
                </option>
              )}
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.number} {p.name}
                </option>
              ))}
            </Select>
          }
        />
      )}
      <MoneyLockNotice locked={data.money.locked} />
      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-line bg-sheet p-3">
        <PeriodPicker p={period} />
        <div className="ml-auto flex flex-wrap gap-2">
          {data.canConfigure && (
            <Button size="sm" icon={<Settings2 className="size-3.5" />} onClick={() => setConfigOpen(true)}>
              {t('analysis.settings')}
            </Button>
          )}
          <Button size="sm" icon={<Download className="size-3.5" />} loading={exportM.isPending} onClick={() => setExportOpen(true)}>
            {t('analysis.export.button')}
          </Button>
        </div>
        <FilterBar data={data} dims={dims} filters={filters} onChange={set} />
        {active.length > 0 && (
          <div className="flex w-full flex-wrap items-center gap-1.5">
            {active.map(([k, v]) => (
              <button
                key={k}
                type="button"
                onClick={() => set(k, '')}
                className="inline-flex items-center gap-1 rounded-full border border-accent/40 bg-accent-soft px-2 py-0.5 text-xs text-accent hover:bg-accent/15"
                title={t('analysis.removeFilter')}
              >
                {dims.label(k)}: {dims.valueLabel(k, v)}
                <X className="size-3" aria-hidden />
              </button>
            ))}
            <button type="button" onClick={clear} className="text-xs text-graphite underline-offset-2 hover:text-ink hover:underline">
              {t('analysis.clearFilters')}
            </button>
          </div>
        )}
      </div>
      <OutOfScopeNotice tasks={data.tasks} fields={data.fields} onEdit={data.canConfigure ? () => setScopeField(data.tasks.find((x) => x.outOfScope?.length)?.outOfScope?.[0]?.field ?? null) : undefined} />
      <Tabs
        value={tab}
        onChange={setTab}
        tabs={[
          { id: 'overview', label: t('analysis.tabs.overview'), icon: <ChartColumnBig className="size-4" /> },
          { id: 'building', label: t('analysis.tabs.building'), icon: <Building2 className="size-4" /> },
          { id: 'matrix', label: t('analysis.tabs.matrix'), icon: <Grid3x3 className="size-4" /> },
          { id: 'schedule', label: t('analysis.tabs.schedule'), icon: <CalendarClock className="size-4" /> },
          { id: 'team', label: t('analysis.tabs.team'), icon: <Users className="size-4" /> },
          { id: 'tasks', label: t('analysis.tabs.tasks'), icon: <ListChecks className="size-4" /> },
          { id: 'cost', label: t('analysis.tabs.cost'), icon: <Wallet className="size-4" />, hidden: !withMoney },
        ]}
      />
      {tab === 'overview' && <OverviewTab filters={filters} data={data} model={model} dims={dims} onFilter={setMany} />}
      {tab === 'building' && <BuildingTab data={data} model={model} dims={dims} filters={filters} onFilter={setMany} onConfigure={data.canConfigure ? () => setConfigOpen(true) : undefined} />}
      {tab === 'matrix' && <MatrixTab data={data} model={model} dims={dims} onFilter={setMany} />}
      {tab === 'schedule' && <ScheduleTab data={data} model={model} dims={dims} link={link} />}
      {tab === 'team' && <TeamTab data={data} model={model} dims={dims} />}
      {tab === 'tasks' && <TasksTable data={data} model={model} link={link} />}
      {tab === 'cost' && withMoney && <CostTab data={data} model={model} dims={dims} />}
      <p className="text-xs text-graphite">{t('analysis.definitions')}</p>
      <ConfigDialog
        open={configOpen}
        onClose={() => setConfigOpen(false)}
        data={data}
        onScope={(id) => {
          setConfigOpen(false);
          setScopeField(id);
        }}
      />
      <FieldScopeDialog projectId={data.project.id} fieldId={scopeField} open={!!scopeField} onClose={() => setScopeField(null)} />
      <ExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        data={data}
        model={model}
        withMoney={withMoney}
        parts={parts}
        setParts={setParts}
        busy={exportM.isPending}
        onExport={(format) => {
          setExportOpen(false);
          if (format === 'pdf') setReport(true);
          else if (format === 'html') setHtmlReport(true);
          else
            exportM
              .mutateAsync({
                projectId,
                from: period.range.from,
                to: period.range.to,
                filters,
                groupDim: data.config.trade ? 'trade' : 'category',
                lang: lang(),
                sections: excelSections(parts),
              })
              .then(saveFile)
              .catch(toast.error);
        }}
      />
      <HtmlReportHost open={htmlReport} onDone={() => setHtmlReport(false)} title={`${t('analysis.projectReport')}: ${data.project.number} ${data.project.name}`} fileName={`Projektanalyse_${safeName(`${data.project.number}_${data.project.name}`)}_${data.today}.html`}>
        <ProjectReport data={data} model={model} dims={dims} filters={active} periodLabel={periodText(t('analysis.periods.all'), period.range)} parts={parts} link={link} />
      </HtmlReportHost>
      <ReportHost open={report} onClose={() => setReport(false)} title={t('analysis.projectReport')}>
        <ProjectReport data={data} model={model} dims={dims} filters={active} periodLabel={periodText(t('analysis.periods.all'), period.range)} parts={parts} link={link} />
      </ReportHost>
    </div>
  );
}

type Dims = ReturnType<typeof useDims>;
type Model = { opts: AnalysisOptions; tasks: AnalysisTask[]; hours: AnalysisData['hours']; total: GroupStat };

function FilterBar({ data, dims, filters, onChange }: { data: AnalysisData; dims: Dims; filters: Record<string, string>; onChange: (dim: string, value: string) => void }) {
  const { t } = useTranslation();
  const values = useMemo(() => {
    const out: Record<string, string[]> = {};
    for (const dim of dims.list) {
      if (dim.key === 'person') out.person = data.persons.map((p) => p.id).sort((a, b) => dims.personName(a).localeCompare(dims.personName(b)));
      else out[dim.key] = dimValues(data.tasks, dim.key, data.config);
    }
    return out;
  }, [data, dims]);
  const shown = dims.list.filter((dim) => (values[dim.key]?.length ?? 0) > 1 || filters[dim.key]);
  return (
    <div className="grid w-full grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5 2xl:grid-cols-8">
      {shown.map((dim) => (
        <Field key={dim.key} label={dim.label}>
          <Select className="h-8" value={filters[dim.key] ?? ''} onChange={(e) => onChange(dim.key, e.target.value)} aria-label={dim.label}>
            <option value="">{t('common.all')}</option>
            {(values[dim.key] ?? []).map((v) => (
              <option key={v} value={v}>
                {dims.valueLabel(dim.key, v)}
              </option>
            ))}
          </Select>
        </Field>
      ))}
    </div>
  );
}

/** Main grouping of the overview: the trade if configured, otherwise the Forma category. */
const mainDim = (config: AnalysisConfig): AnalysisDim => (config.trade ? 'trade' : config.phase ? 'phase' : 'category');

function KpiTiles({ data, total }: { data: AnalysisData; total: GroupStat }) {
  const { t } = useTranslation();
  const by = data.config.progressBy;
  const p = progressOf(total, by);
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-8">
      <Tile
        label={t('analysis.kpi.progress')}
        value={pctOr(p)}
        hint={by === 'plan' ? t('analysis.kpi.byPlan', { done: h(total.donePlannedMinutes, 0), plan: h(total.plannedMinutes, 0) }) : t('analysis.kpi.doneOf', { done: num(total.done), tasks: num(total.tasks) })}
      />
      <Tile label={t('analysis.schedule.label')} value={<ScheduleBadge state={total.schedule} className="text-sm" />} hint={t('analysis.kpi.overdueSoon', { overdue: total.overdue, soon: total.dueSoon })} />
      <Tile label={t('analysis.kpi.overdue')} value={num(total.overdue)} tone={total.overdue ? 'bad' : undefined} hint={t('analysis.kpi.withDue', { count: total.withDue })} />
      <Tile label={t('analysis.kpi.actual')} value={`${h(total.actualMinutes, 0)} h`} hint={t('analysis.kpi.lifetime', { hours: h(total.lifetimeMinutes, 0) })} />
      <Tile label={t('analysis.kpi.plan')} value={`${h(total.plannedMinutes, 0)} h`} hint={t('analysis.kpi.plannedTasks', { count: total.plannedTasks, tasks: total.tasks })} />
      <Tile label={t('analysis.kpi.planUse')} value={pctOr(total.planUse)} tone={total.planUse > 1 ? 'warn' : undefined} hint={t('analysis.kpi.planUseHint')} />
      <Tile label={t('analysis.kpi.planRatio')} value={pctOr(total.planRatio)} hint={t('analysis.kpi.planRatioHint')} />
      <Tile
        label={t('analysis.kpi.onTime')}
        value={pctOr(total.onTime)}
        hint={Number.isFinite(total.avgCloseDays) ? t('analysis.kpi.avgClose', { days: num(total.avgCloseDays, 0) }) : t('analysis.kpi.noneDone')}
      />
    </div>
  );
}

function OverviewTab({ data, model, dims, filters, onFilter }: { data: AnalysisData; model: Model; dims: Dims; filters: Record<string, string>; onFilter: (p: Record<string, string>) => void }) {
  const { t } = useTranslation();
  const main = mainDim(data.config);
  const groups = useMemo(() => groupBy(model.tasks, model.hours, main, model.opts, false), [model, main]);
  const points = useMemo(() => timeline(model.tasks, model.hours, model.opts), [model]);
  const bv = useMemo(() => buildingView(model.tasks, model.hours, model.opts, false, data.project.name), [model, data.project.name]);
  const overdue = useMemo(() => overdueTasks(model.tasks, data.today, 10), [model, data.today]);
  return (
    <div className="flex flex-col gap-4">
      <KpiTiles data={data} total={model.total} />
      <div className="grid gap-4 xl:grid-cols-2">
        <Panel title={t('analysis.progressBy', { dim: dims.label(main) })}>
          <GroupProgress groups={groups} progressBy={data.config.progressBy} head={dims.label(main)} valueLabel={(v) => dims.valueLabel(main, v)} onSelect={(k) => onFilter({ [main]: k })} />
        </Panel>
        <Panel title={t('analysis.buildings')} actions={<ProgressLegend />}>
          {bv.empty ? <p className="text-sm text-graphite">{t('analysis.noFloors')}</p> : <BuildingSvg view={bv} progressBy={data.config.progressBy} maxWidth={720} onSelect={(b, f) => onFilter({ building: data.config.building.kind !== 'none' ? b : '', floor: f ?? '' })} />}
        </Panel>
      </div>
      {/* a floor with zones (third tier of the Forma locations): the floor alone with its zones and the work left (request 13) */}
      {filters.floor && data.config.zone && data.config.zone.kind !== 'none' && (
        <ZonePlan data={data} model={model} building={filters.building || null} floor={filters.floor} onFilter={onFilter} selected={filters.zone ?? ''} />
      )}
      <Panel title={t('analysis.burn.title')}>
        <BurnUp points={points} tasks={model.tasks} />
      </Panel>
      <div className="grid gap-4 xl:grid-cols-2">
        <Panel title={t('analysis.hoursPerWeek')}>
          <WeekBars
            showCapacity={false}
            head={t('common.week')}
            data={points.map((p) => ({ label: `${t('kpi.weekPrefix')} ${kw(p.week)}`, axis: String(kw(p.week)), booked: p.minutes, billable: p.billable, capacity: 0, current: p.week === weekStart(data.today) }))}
          />
        </Panel>
        <Panel title={t('analysis.overdueTop')}>
          <OverdueList items={overdue} today={data.today} />
        </Panel>
      </div>
    </div>
  );
}

function BuildingTab({
  data,
  model,
  dims,
  filters,
  onFilter,
  onConfigure,
}: {
  data: AnalysisData;
  model: Model;
  dims: Dims;
  filters: Record<string, string>;
  onFilter: (p: Record<string, string>) => void;
  onConfigure?: () => void;
}) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<BuildingMode>('progress');
  // the picture shows all floors of the filtered data except the floor filter itself (a click selects a floor)
  const base = useMemo(() => {
    const { floor: _f, building: _b, ...rest } = filters;
    return filterData(data.tasks, data.hours, rest, data.config);
  }, [data, filters]);
  const bv = useMemo(() => buildingView(base.tasks, base.hours, model.opts, false, data.project.name), [base, model.opts, data.project.name]);
  const selected = filters.floor || filters.building ? { building: filters.building ?? '', floor: filters.floor ?? null } : null;
  const detailDim = data.config.trade ? 'trade' : 'category';
  const detail = useMemo(() => groupBy(model.tasks, model.hours, detailDim, model.opts, false), [model, detailDim]);
  if (bv.empty) {
    return (
      <EmptyState action={onConfigure && <Button onClick={onConfigure}>{t('analysis.settings')}</Button>}>
        <p className="font-medium text-ink">{t('analysis.noFloorsTitle')}</p>
        <p className="mt-1">{t('analysis.noFloors')}</p>
      </EmptyState>
    );
  }
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Segmented
          label={t('analysis.colourBy')}
          value={mode}
          onChange={setMode}
          items={[
            { id: 'progress', label: t('analysis.modes.progress') },
            { id: 'schedule', label: t('analysis.modes.schedule') },
            { id: 'hours', label: t('analysis.modes.hours') },
          ]}
        />
        {mode === 'schedule' ? <ScheduleLegend /> : mode === 'hours' ? <HoursLegend /> : <ProgressLegend />}
      </div>
      <Panel bodyClassName="p-3">
        <BuildingSvg
          view={bv}
          progressBy={data.config.progressBy}
          mode={mode}
          selected={selected}
          onSelect={(b, f) => {
            const sameFloor = selected && selected.building === (data.config.building.kind !== 'none' ? b : '') && selected.floor === f;
            onFilter(sameFloor ? { building: '', floor: '' } : { building: data.config.building.kind !== 'none' ? b : '', floor: f ?? '' });
          }}
        />
        <p className="mt-2 text-xs text-graphite">{t('analysis.buildingHint')}</p>
      </Panel>
      {filters.floor && data.config.zone && data.config.zone.kind !== 'none' && (
        <ZonePlan data={data} model={model} building={filters.building || null} floor={filters.floor} onFilter={onFilter} selected={filters.zone ?? ''} />
      )}
      {(filters.floor || filters.building) && (
        <Panel title={t('analysis.detailOf', { place: [filters.building && dims.valueLabel('building', filters.building), filters.floor && dims.valueLabel('floor', filters.floor)].filter(Boolean).join(' · '), dim: dims.label(detailDim) })}>
          <GroupProgress groups={detail} progressBy={data.config.progressBy} head={dims.label(detailDim)} valueLabel={(v) => dims.valueLabel(detailDim, v)} onSelect={(k) => onFilter({ [detailDim]: k })} />
        </Panel>
      )}
    </div>
  );
}

/**
 * One floor with its zones (round 5, request 13): with a zone source (third tier of the Forma locations) the selected floor
 * is drawn on its own, its zones side by side, each with the share of finished work and the work left. A click on a zone
 * filters the analysis to it; the detail below then shows the trades of that zone.
 */
function ZonePlan({ data, model, building, floor, selected, onFilter }: { data: AnalysisData; model: Model; building: string | null; floor: string; selected: string; onFilter: (p: Record<string, string>) => void }) {
  const { t } = useTranslation();
  // the zones of the whole floor, also while one zone is selected
  const base = useMemo(() => {
    const { zone: _z, ...rest } = Object.fromEntries(Object.entries({ building: building ?? '', floor }).filter(([, v]) => v));
    return filterData(data.tasks, data.hours, rest, data.config);
  }, [data, building, floor]);
  const zv = useMemo(() => zoneView(base.tasks, base.hours, model.opts, false, building, floor), [base, model.opts, building, floor]);
  if (!zv) return null;
  const tiles = [...zv.zones, ...(zv.general ? [zv.general] : [])];
  const share = (g: { tasks: number; done: number; plannedMinutes: number; donePlannedMinutes?: number }) =>
    data.config.progressBy === 'plan' && g.plannedMinutes ? (g.donePlannedMinutes ?? 0) / g.plannedMinutes : g.tasks ? g.done / g.tasks : 0;
  return (
    <Panel title={t('analysis.zones.title', { floor: [building, floor].filter(Boolean).join(' · ') })}>
      {tiles.length === 0 ? (
        <p className="text-sm text-graphite">{t('analysis.zones.none')}</p>
      ) : (
        <div role="list" className="grid gap-2" style={{ gridTemplateColumns: `repeat(${Math.min(4, Math.max(1, tiles.length))}, minmax(0, 1fr))` }}>
          {tiles.map((z) => {
            const p = share(z);
            const isSel = selected === (z.key === NONE ? NONE : z.key);
            const label = z.key === NONE ? t('analysis.zones.noZone') : z.label;
            return (
              <button
                role="listitem"
                key={z.key}
                type="button"
                onClick={() => onFilter({ zone: isSel ? '' : z.key === NONE ? NONE : z.key })}
                aria-pressed={isSel}
                className={clsx('relative flex min-h-24 flex-col justify-between overflow-hidden rounded-md border p-2 text-left', isSel ? 'border-ink' : 'border-line hover:border-line-strong')}
                title={t('analysis.zones.tileHint', { zone: label, done: z.done, tasks: z.tasks, open: z.open })}
              >
                <span aria-hidden className="absolute inset-y-0 left-0 bg-good-soft" style={{ width: `${Math.round(p * 100)}%` }} />
                <span className="relative font-medium">{label}</span>
                <span className="relative text-xs text-graphite">
                  {t('analysis.zones.progress', { pct: Math.round(p * 100) })} · {t('analysis.zones.left', { count: z.open })}
                </span>
              </button>
            );
          })}
        </div>
      )}
      <p className="mt-2 text-xs text-graphite">{t('analysis.zones.hint')}</p>
    </Panel>
  );
}

function MatrixTab({ data, model, dims, onFilter }: { data: AnalysisData; model: Model; dims: Dims; onFilter: (p: Record<string, string>) => void }) {
  const { t } = useTranslation();
  const choices = dims.list.filter((x) => x.key !== 'person');
  const [rows, setRows] = useState<string>(data.config.floor.kind !== 'none' ? 'floor' : data.config.building.kind !== 'none' ? 'building' : 'category');
  const [cols, setCols] = useState<string>(data.config.trade ? 'trade' : data.config.phase ? 'phase' : 'type');
  const [mode, setMode] = useState<'progress' | 'hours'>('progress');
  const m = useMemo(() => matrixModel(model.tasks, model.hours, rows, cols === rows ? 'stage' : cols, model.opts, false), [model, rows, cols]);
  const colDim = cols === rows ? 'stage' : cols;
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-3">
        <Field label={t('analysis.rows')} className="w-56">
          <Select className="h-8" value={rows} onChange={(e) => setRows(e.target.value)}>
            {choices.map((c) => (
              <option key={c.key} value={c.key}>
                {c.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('analysis.cols')} className="w-56">
          <Select className="h-8" value={cols} onChange={(e) => setCols(e.target.value)}>
            {choices.map((c) => (
              <option key={c.key} value={c.key}>
                {c.label}
              </option>
            ))}
          </Select>
        </Field>
        <Segmented
          label={t('analysis.colourBy')}
          value={mode}
          onChange={setMode}
          items={[
            { id: 'progress', label: t('analysis.modes.progress') },
            { id: 'hours', label: t('analysis.modes.hours') },
          ]}
        />
        <ProgressLegend className="ml-auto" />
      </div>
      <Panel bodyClassName="p-3">
        <MatrixTable
          model={m}
          progressBy={data.config.progressBy}
          rowLabel={dims.label(rows)}
          colLabel={dims.label(colDim)}
          valueLabel={(which, v) => dims.valueLabel(which === 'row' ? rows : colDim, v)}
          mode={mode}
          onCell={(r, c) => onFilter({ ...(r !== null ? { [rows]: r } : {}), ...(c !== null && colDim !== 'stage' ? { [colDim]: c } : {}) })}
        />
        <p className="mt-2 text-xs text-graphite">{t('analysis.matrixHint')}</p>
      </Panel>
    </div>
  );
}

function ScheduleTab({ data, model, dims, link }: { data: AnalysisData; model: Model; dims: Dims; link: (id: string) => string | null }) {
  const { t } = useTranslation();
  const choices = dims.list.filter((x) => x.key !== 'person' && x.key !== 'stage');
  const [group, setGroup] = useState<string>(mainDim(data.config));
  const [openOnly, setOpenOnly] = useState(false);
  const limit = 400;
  const items = useMemo(() => {
    const ts = openOnly ? model.tasks.filter((x) => x.statusGroup === 'active') : model.tasks;
    return ganttItems(ts, group, model.opts, limit);
  }, [model, group, openOnly]);
  const overdue = useMemo(() => overdueTasks(model.tasks, data.today, 30), [model, data.today]);
  const without = model.tasks.filter((x) => !x.archived && !x.startDate && !x.dueDate).length;
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-4">
        <Field label={t('analysis.groupBy')} className="w-56">
          <Select className="h-8" value={group} onChange={(e) => setGroup(e.target.value)}>
            {choices.map((c) => (
              <option key={c.key} value={c.key}>
                {c.label}
              </option>
            ))}
          </Select>
        </Field>
        <Checkbox className="mb-1.5" label={t('analysis.openOnly')} checked={openOnly} onChange={(e) => setOpenOnly(e.target.checked)} />
        {without > 0 && <span className="mb-1.5 text-xs text-graphite">{t('analysis.gantt.without', { count: without })}</span>}
      </div>
      <Panel bodyClassName="p-3">
        <Gantt items={items} today={data.today} groupLabel={(v) => dims.valueLabel(group, v || NONE)} formaLink={link} limit={limit} />
      </Panel>
      <Panel title={t('analysis.overdueAllProject', { count: overdue.length })}>
        <OverdueList
          items={overdue}
          today={data.today}
          extra={(task) => {
            const where = [taskDimValue(task, 'building', data.config), taskDimValue(task, 'floor', data.config), taskDimValue(task, 'trade', data.config)].filter(Boolean).join(' · ');
            return where ? ` · ${where}` : null;
          }}
        />
      </Panel>
    </div>
  );
}

function TeamTab({ data, model, dims }: { data: AnalysisData; model: Model; dims: Dims }) {
  const { t } = useTranslation();
  const choices = dims.list.filter((x) => x.key !== 'person');
  const [col, setCol] = useState<string>(mainDim(data.config));
  const pm = useMemo(() => personMatrix(model.tasks, model.hours, col, model.opts), [model, col]);
  const persons = useMemo(() => groupBy(model.tasks, model.hours, 'person', model.opts, false), [model]);
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-4">
        <Field label={t('analysis.cols')} className="w-56">
          <Select className="h-8" value={col} onChange={(e) => setCol(e.target.value)}>
            {choices.map((c) => (
              <option key={c.key} value={c.key}>
                {c.label}
              </option>
            ))}
          </Select>
        </Field>
        <span className="mb-1.5 text-xs text-graphite">{t('analysis.teamHint')}</span>
      </div>
      <Panel title={t('analysis.whoWhere')} bodyClassName="p-3">
        <PersonHours pm={pm} colLabel={dims.label(col)} valueLabel={(v) => dims.valueLabel(col, v)} personName={dims.personName} />
      </Panel>
      <Panel title={t('analysis.personTasks')} bodyClassName="p-0">
        {persons.length === 0 ? (
          <p className="p-4 text-sm text-graphite">{t('analysis.noHours')}</p>
        ) : (
          <div className="scroll-x">
            <table className="w-full min-w-[640px] text-sm">
              <thead className="border-b border-line bg-sheet-2">
                <tr>
                  <th className={th}>{t('analysis.dims.person')}</th>
                  <th className={clsx(th, 'text-right')}>{t('analysis.kpi.actual')}</th>
                  <th className={clsx(th, 'text-right')}>{t('analysis.kpi.tasks')}</th>
                  <th className={clsx(th, 'w-48')}>{t('analysis.kpi.progress')}</th>
                  <th className={clsx(th, 'text-right')}>{t('analysis.kpi.overdue')}</th>
                </tr>
              </thead>
              <tbody>
                {persons.map((p) => (
                  <tr key={p.key} className="border-b border-line/60">
                    <td className={td}>{dims.personName(p.key)}</td>
                    <td className={clsx(td, 'num text-right')}>{h(p.actualMinutes, 1)} h</td>
                    <td className={clsx(td, 'num text-right')}>{num(p.tasks)}</td>
                    <td className={td}>
                      <div className="flex items-center gap-2">
                        <StageBar s={p} className="flex-1" />
                        <span className="num w-10 text-right text-xs">{pctOr(p.completion)}</span>
                      </div>
                    </td>
                    <td className={clsx(td, 'num text-right', p.overdue > 0 && 'font-semibold text-bad')}>{num(p.overdue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}

type SortKey = 'ref' | 'due' | 'plan' | 'actual' | 'overdue';

function TasksTable({ data, model, link }: { data: AnalysisData; model: Model; link: (id: string) => string | null }) {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<SortKey>('overdue');
  const [limit, setLimit] = useState(200);
  const cfg = data.config;
  const lifetime = useMemo(() => {
    const m = new Map<string, number>();
    for (const x of model.hours) m.set(x.taskId, (m.get(x.taskId) ?? 0) + x.minutes);
    return m;
  }, [model.hours]);
  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    const list = model.tasks.filter((x) => !x.archived && (!term || `${x.ref} ${x.title}`.toLowerCase().includes(term)));
    const late = (x: AnalysisTask) => (x.statusGroup === 'active' && x.dueDate && x.dueDate < data.today ? x.dueDate : '9999');
    const cmp: Record<SortKey, (a: AnalysisTask, b: AnalysisTask) => number> = {
      ref: (a, b) => a.ref.localeCompare(b.ref, 'de', { numeric: true }),
      due: (a, b) => (a.dueDate ?? '9999').localeCompare(b.dueDate ?? '9999'),
      plan: (a, b) => (b.plannedMinutes ?? -1) - (a.plannedMinutes ?? -1),
      actual: (a, b) => (lifetime.get(b.id) ?? 0) - (lifetime.get(a.id) ?? 0),
      overdue: (a, b) => late(a).localeCompare(late(b)) || (a.dueDate ?? '9999').localeCompare(b.dueDate ?? '9999'),
    };
    return list.sort(cmp[sort]);
  }, [model.tasks, q, sort, lifetime, data.today]);
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-3">
        <Field label={t('common.search')} className="w-72">
          <Input className="h-8" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('tasks.search')} />
        </Field>
        <Field label={t('analysis.sortBy')} className="w-56">
          <Select className="h-8" value={sort} onChange={(e) => setSort(e.target.value as SortKey)}>
            {(['overdue', 'due', 'ref', 'plan', 'actual'] as const).map((k) => (
              <option key={k} value={k}>
                {t(`analysis.sort.${k}`)}
              </option>
            ))}
          </Select>
        </Field>
        <span className="mb-1.5 ml-auto text-sm text-graphite">{t('tasks.count', { count: rows.length })}</span>
      </div>
      <ScrollTable label={t('analysis.tabs.tasks')}>
        <table className="w-full min-w-[1100px] text-sm">
          <thead className="border-b border-line bg-sheet-2">
            <tr>
              <th className={clsx(th, 'min-w-64')}>{t('common.task')}</th>
              {cfg.building.kind !== 'none' && <th className={th}>{t('analysis.dims.building')}</th>}
              {cfg.floor.kind !== 'none' && <th className={th}>{t('analysis.dims.floor')}</th>}
              {cfg.trade && <th className={th}>{t('analysis.dims.trade')}</th>}
              {cfg.phase && <th className={th}>{t('analysis.dims.phase')}</th>}
              <th className={th}>{t('common.status')}</th>
              <th className={th}>{t('analysis.startDue')}</th>
              <th className={clsx(th, 'text-right')}>{t('analysis.kpi.plan')}</th>
              <th className={clsx(th, 'text-right')}>{t('analysis.kpi.lifetimeShort')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, limit).map((x) => {
              const late = x.statusGroup === 'active' && x.dueDate && x.dueDate < data.today;
              const url = link(x.id);
              const actual = lifetime.get(x.id) ?? 0;
              return (
                <tr key={x.id} className="border-b border-line/60">
                  <td className={td}>
                    <span className="num font-semibold text-accent">{x.ref}</span> {x.title}
                    {x.outOfScope?.length ? (
                      <span
                        role="img"
                        className="ml-1 inline-flex align-middle text-warn"
                        title={t('scope.taskMark', { list: x.outOfScope.map((o) => `${data.fields.find((f) => f.id === o.field)?.title ?? o.field} „${o.value}“`).join(', ') })}
                        aria-label={t('scope.taskMark', { list: x.outOfScope.map((o) => `${data.fields.find((f) => f.id === o.field)?.title ?? o.field} „${o.value}“`).join(', ') })}
                      >
                        <AlertTriangle className="size-3.5" aria-hidden />
                      </span>
                    ) : null}
                    {url && (
                      <a href={url} target="_blank" rel="noreferrer" className="ml-1 text-xs text-graphite hover:text-accent">
                        Forma
                      </a>
                    )}
                    <div className="text-xs text-graphite">{[x.categoryTitle, x.typeTitle].filter(Boolean).join(' · ')}</div>
                  </td>
                  {cfg.building.kind !== 'none' && <td className={clsx(td, 'text-graphite')}>{taskDimValue(x, 'building', cfg)}</td>}
                  {cfg.floor.kind !== 'none' && <td className={clsx(td, 'text-graphite')}>{taskDimValue(x, 'floor', cfg)}</td>}
                  {cfg.trade && <td className={clsx(td, 'text-graphite')}>{taskDimValue(x, 'trade', cfg)}</td>}
                  {cfg.phase && <td className={clsx(td, 'text-graphite')}>{taskDimValue(x, 'phase', cfg)}</td>}
                  <td className={clsx(td, 'whitespace-nowrap')}>{t(`task.status.${x.status as 'open'}`, { defaultValue: x.status })}</td>
                  <td className={clsx(td, 'num whitespace-nowrap', late && 'font-semibold text-bad')}>
                    {x.startDate ? d(x.startDate) : '-'} - {x.dueDate ? d(x.dueDate) : '-'}
                    {late && <AlarmClock className="ml-1 inline size-3.5" aria-label={t('analysis.gantt.overdue')} />}
                  </td>
                  <td className={clsx(td, 'num text-right')}>{x.plannedMinutes ? h(x.plannedMinutes, 1) : '-'}</td>
                  <td className={clsx(td, 'num text-right', x.plannedMinutes && actual > x.plannedMinutes && 'font-semibold text-warn')}>{actual ? h(actual, 1) : '-'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </ScrollTable>
      {rows.length > limit && (
        <Button className="self-start" onClick={() => setLimit((l) => l + 400)}>
          {t('analysis.showMore', { count: rows.length - limit })}
        </Button>
      )}
    </div>
  );
}

function CostTab({ data, model, dims }: { data: AnalysisData; model: Model; dims: Dims }) {
  const { t } = useTranslation();
  const choices = dims.list.filter((x) => x.key !== 'stage');
  const [dim, setDim] = useState<string>(mainDim(data.config));
  const groups = useMemo(() => groupBy(model.tasks, model.hours, dim, model.opts, true).sort((a, b) => (b.costCents ?? 0) - (a.costCents ?? 0)), [model, dim]);
  const max = Math.max(1, ...groups.map((g) => Math.max(g.costCents ?? 0, g.salesCents ?? 0)));
  const total = groupStat('all', '', model.tasks, model.hours, model.opts, true);
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {data.money.cost && <Tile label={t('analysis.kpi.cost')} value={money(total.costCents)} hint={t('analysis.kpi.costHint')} />}
        {data.money.sales && <Tile label={t('analysis.kpi.sales')} value={money(total.salesCents)} hint={t('analysis.kpi.salesHint')} />}
        {data.money.cost && data.money.sales && (
          <Tile label={t('analysis.kpi.margin')} value={money((total.salesCents ?? 0) - (total.costCents ?? 0))} hint={t('analysis.kpi.marginHint')} />
        )}
        {data.project.feeTotalCents !== null && <Tile label={t('analysis.kpi.fee')} value={money(data.project.feeTotalCents)} hint={t('analysis.kpi.feeHint')} />}
      </div>
      {total.costMissingMinutes > 0 && <Notice tone="warn">{t('analysis.kpi.costMissing', { hours: h(total.costMissingMinutes, 1) })}</Notice>}
      <div className="flex flex-wrap items-end gap-3">
        <Field label={t('analysis.groupBy')} className="w-56">
          <Select className="h-8" value={dim} onChange={(e) => setDim(e.target.value)}>
            {choices.map((c) => (
              <option key={c.key} value={c.key}>
                {c.label}
              </option>
            ))}
          </Select>
        </Field>
        <div className="mb-1.5 flex items-center gap-4 text-xs text-graphite">
          {data.money.cost && (
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-2.5 w-4 rounded-[3px] bg-viz-strong" /> {t('analysis.kpi.cost')}
            </span>
          )}
          {data.money.sales && (
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-2.5 w-4 rounded-[3px] bg-viz-soft" /> {t('analysis.kpi.sales')}
            </span>
          )}
        </div>
      </div>
      <Panel bodyClassName="p-3">
        <ul className="flex flex-col gap-1.5">
          {groups.map((g) => (
            <li key={g.key} className="grid grid-cols-[minmax(6rem,14rem)_minmax(0,1fr)_7rem] items-center gap-3 text-sm">
              <span className="truncate">{dims.valueLabel(dim, g.key)}</span>
              <span className="flex flex-col gap-0.5">
                {data.money.cost && <span className="h-2.5 rounded-r-[3px] bg-viz-strong" style={{ width: `${((g.costCents ?? 0) / max) * 100}%` }} title={money(g.costCents)} />}
                {data.money.sales && <span className="h-2.5 rounded-r-[3px] bg-viz-soft" style={{ width: `${((g.salesCents ?? 0) / max) * 100}%` }} title={money(g.salesCents)} />}
              </span>
              <span className="num text-right text-xs">
                {data.money.cost && <span className="block">{money(g.costCents)}</span>}
                {data.money.sales && <span className="block text-graphite">{money(g.salesCents)}</span>}
              </span>
            </li>
          ))}
        </ul>
        <p className="mt-2 text-xs text-graphite">{t('analysis.costNote')}</p>
      </Panel>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Settings                                                             */
/* ------------------------------------------------------------------ */

function sourceValue(s: AnalysisSource) {
  return s.kind === 'attr' ? `attr:${s.id}` : s.kind === 'loc' ? `loc:${s.level}` : 'none';
}
function parseSource(v: string): AnalysisSource {
  if (v.startsWith('attr:')) return { kind: 'attr', id: v.slice(5) };
  if (v === 'loc:1' || v === 'loc:2' || v === 'loc:3') return { kind: 'loc', level: Number(v.slice(4)) as 1 | 2 | 3 };
  return { kind: 'none' };
}

function ConfigDialog({ open, onClose, data, onScope }: { open: boolean; onClose: () => void; data: AnalysisData; onScope: (fieldId: string) => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const save = useM('analysis.setConfig', { invalidate: ['analysis.', 'projects.'] });
  const [c, setC] = useState(data.config);
  useEffect(() => {
    if (open) setC(data.config);
  }, [open, data.config]);
  const fields = data.fields;
  const sourceOptions = (
    <>
      <option value="none">{t('analysis.cfg.none')}</option>
      {fields.map((f) => (
        <option key={f.id} value={`attr:${f.id}`}>
          {t('analysis.cfg.field', { title: f.title })}
        </option>
      ))}
      {data.hasLocations && <option value="loc:1">{t('analysis.cfg.loc1')}</option>}
      {data.hasLocations && <option value="loc:2">{t('analysis.cfg.loc2')}</option>}
      {data.hasLocations && <option value="loc:3">{t('analysis.cfg.loc3')}</option>}
    </>
  );
  const fieldOptions = (
    <>
      <option value="">{t('analysis.cfg.none')}</option>
      {fields.map((f) => (
        <option key={f.id} value={f.id}>
          {f.title}
        </option>
      ))}
    </>
  );
  const run = (config: Partial<AnalysisConfig> | null) =>
    save
      .mutateAsync({ projectId: data.project.id, config })
      .then(() => {
        toast.ok(t('common.saved'));
        onClose();
      })
      .catch(toast.error);
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('analysis.settings')}
      footer={
        <>
          <Button className="mr-auto" onClick={() => run(null)} loading={save.isPending}>
            {t('analysis.cfg.reset')}
          </Button>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" loading={save.isPending} onClick={() => run(c)}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm text-graphite">{t('analysis.cfg.intro')}</p>
        {fields.length === 0 && <Notice tone="warn">{t('analysis.cfg.noFields')}</Notice>}
        <Field label={t('analysis.dims.building')} hint={t('analysis.cfg.buildingHint')}>
          <Select value={sourceValue(c.building)} onChange={(e) => setC({ ...c, building: parseSource(e.target.value) })}>
            {sourceOptions}
          </Select>
        </Field>
        <Field label={t('analysis.dims.floor')} hint={t('analysis.cfg.floorHint')}>
          <Select value={sourceValue(c.floor)} onChange={(e) => setC({ ...c, floor: parseSource(e.target.value) })}>
            {sourceOptions}
          </Select>
        </Field>
        <Field label={t('analysis.dims.zone')} hint={t('analysis.cfg.zoneHint')}>
          <Select value={sourceValue(c.zone ?? { kind: 'none' })} onChange={(e) => setC({ ...c, zone: parseSource(e.target.value) })}>
            {sourceOptions}
          </Select>
        </Field>
        <Field label={t('analysis.dims.trade')}>
          <Select value={c.trade ?? ''} onChange={(e) => setC({ ...c, trade: e.target.value || null })}>
            {fieldOptions}
          </Select>
        </Field>
        <Field label={t('analysis.dims.phase')}>
          <Select value={c.phase ?? ''} onChange={(e) => setC({ ...c, phase: e.target.value || null })}>
            {fieldOptions}
          </Select>
        </Field>
        <Field label={t('analysis.cfg.progressBy')} hint={t('analysis.cfg.progressByHint')}>
          <Select value={c.progressBy} onChange={(e) => setC({ ...c, progressBy: e.target.value as 'count' })}>
            <option value="count">{t('analysis.cfg.byCount')}</option>
            <option value="plan">{t('analysis.cfg.byPlan')}</option>
          </Select>
        </Field>
        <p className="text-xs text-graphite">{t('analysis.cfg.fieldsHint')}</p>
        {fields.length > 0 && (
          // which values of a field the project uses, e.g. the floors of each building (request 20)
          <div className="rounded-md border border-line p-2">
            <p className="mb-1 text-xs text-graphite">{t('scope.intro')}</p>
            <div className="flex flex-wrap gap-1">
              {fields.map((f) => (
                <FieldScopeButton key={f.id} label={f.title} onClick={() => onScope(f.id)} />
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

/* ------------------------------------------------------------------ */
/* Report (PDF)                                                         */
/* ------------------------------------------------------------------ */

/**
 * Report for the PDF: rendered into the page body and printed alone (lib/print.ts). In the demo the print dialog of
 * the viewer may be blocked, so the report is shown in a preview with a print button.
 */
/** Parts of the project report (PDF, HTML) and of the Excel file; the export dialog chooses them. */
type ReportPart = 'overview' | 'building' | 'matrix' | 'schedule' | 'team' | 'tasks' | 'cost';
const REPORT_PARTS: ReportPart[] = ['overview', 'building', 'matrix', 'schedule', 'team', 'tasks', 'cost'];
type ExportFormat = 'pdf' | 'html' | 'xlsx';
/** Sheets of the Excel file for the chosen parts (the file has no drawings: building and schedule are in the PDF/HTML). */
function excelSections(parts: Set<ReportPart>): ('overview' | 'groups' | 'tasks' | 'matrix' | 'people')[] {
  const out: ('overview' | 'groups' | 'tasks' | 'matrix' | 'people')[] = [];
  if (parts.has('overview')) out.push('overview', 'groups');
  if (parts.has('tasks')) out.push('tasks');
  if (parts.has('matrix')) out.push('matrix');
  if (parts.has('team')) out.push('people');
  return out;
}

function ExportDialog({
  open,
  onClose,
  data,
  model,
  withMoney,
  parts,
  setParts,
  busy,
  onExport,
}: {
  open: boolean;
  onClose: () => void;
  data: AnalysisData;
  model: Model;
  withMoney: boolean;
  parts: Set<ReportPart>;
  setParts: (p: Set<ReportPart>) => void;
  busy: boolean;
  onExport: (format: ExportFormat) => void;
}) {
  const { t } = useTranslation();
  const [format, setFormat] = useState<ExportFormat>('pdf');
  const hasBuilding = useMemo(() => !buildingView(model.tasks, model.hours, model.opts, false, data.project.name).empty, [model, data.project.name]);
  const hasMatrix = data.config.floor.kind !== 'none' && !!data.config.trade;
  const available: Record<ReportPart, boolean> = {
    overview: true,
    building: hasBuilding && format !== 'xlsx',
    matrix: hasMatrix,
    schedule: format !== 'xlsx',
    team: true,
    tasks: true,
    cost: withMoney && format !== 'xlsx',
  };
  const chosen = REPORT_PARTS.filter((p) => available[p] && parts.has(p));
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('analysis.export.title')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" icon={format === 'xlsx' ? <FileSpreadsheet className="size-4" /> : format === 'html' ? <FileCode2 className="size-4" /> : <Printer className="size-4" />} disabled={chosen.length === 0} loading={busy} onClick={() => onExport(format)}>
            {t(`analysis.export.go.${format}`)}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-sm font-medium">{t('analysis.export.format')}</legend>
          {(['pdf', 'html', 'xlsx'] as const).map((f) => (
            <label key={f} className="flex items-start gap-2 text-sm">
              <input type="radio" name="analysis-export-format" className="mt-0.5 size-4 accent-[var(--accent)]" checked={format === f} onChange={() => setFormat(f)} />
              <span>
                <span className="font-medium">{t(`analysis.export.formats.${f}`)}</span>
                <span className="block text-xs text-graphite">{t(`analysis.export.formatHints.${f}`)}</span>
              </span>
            </label>
          ))}
        </fieldset>
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-sm font-medium">{t('analysis.export.parts')}</legend>
          {REPORT_PARTS.filter((p) => p !== 'cost' || withMoney).map((p) => (
            <Checkbox
              key={p}
              label={t(`analysis.export.part.${p}`)}
              hint={!available[p] ? t(format === 'xlsx' && (p === 'building' || p === 'schedule' || p === 'cost') ? 'analysis.export.notInExcel' : 'analysis.export.notAvailable') : undefined}
              disabled={!available[p]}
              checked={available[p] && parts.has(p)}
              onChange={(e) => {
                const next = new Set(parts);
                if (e.target.checked) next.add(p);
                else next.delete(p);
                setParts(next);
              }}
            />
          ))}
        </fieldset>
        <p className="text-xs text-graphite">{t('analysis.export.filtersHint')}</p>
      </div>
    </Modal>
  );
}

/**
 * Interactive HTML file of a report: the report is drawn once into a hidden element, written into the file with the
 * styles of the application and a small script for the details on hover, and handed to the download.
 */
function ProjectReport({
  data,
  model,
  dims,
  filters,
  periodLabel,
  parts = new Set(REPORT_PARTS),
  link,
}: {
  data: AnalysisData;
  model: Model;
  dims: Dims;
  filters: [string, string][];
  periodLabel: string;
  parts?: Set<ReportPart>;
  link?: (id: string) => string | null;
}) {
  const { t } = useTranslation();
  const { me } = useMe();
  const main = mainDim(data.config);
  const groups = groupBy(model.tasks, model.hours, main, model.opts, false);
  const phaseGroups = data.config.phase && main !== 'phase' ? groupBy(model.tasks, model.hours, 'phase', model.opts, false) : [];
  const bv = buildingView(model.tasks, model.hours, model.opts, false, data.project.name);
  const matrix = data.config.floor.kind !== 'none' && data.config.trade ? matrixModel(model.tasks, model.hours, 'floor', 'trade', model.opts, false) : null;
  const points = timeline(model.tasks, model.hours, model.opts);
  const overdue = overdueTasks(model.tasks, data.today, 15);
  const pm = personMatrix(model.tasks, model.hours, main, model.opts);
  const filterText = filters.map(([k, v]) => `${dims.label(k)}: ${dims.valueLabel(k, v)}`).join(' · ');
  return (
    <div className="flex flex-col gap-4">
      <ReportHeader
        title={`${t('analysis.projectReport')}: ${data.project.number} ${data.project.name}`}
        sub={[data.project.customerName, `${t('analysis.period')}: ${periodLabel}`, filterText && `${t('analysis.filters')}: ${filterText}`].filter(Boolean).join(' · ')}
        company={me.settings.companyName}
      />
      {parts.has('overview') && <KpiTiles data={data} total={model.total} />}
      {parts.has('building') && !bv.empty && (
        <section className="print-avoid-break">
          <h2 className="mb-1 text-sm font-semibold">{t('analysis.buildings')}</h2>
          <BuildingSvg view={bv} progressBy={data.config.progressBy} maxWidth={1000} />
          <ProgressLegend className="mt-1" />
        </section>
      )}
      {parts.has('overview') && (
        <section className="print-avoid-break">
          <h2 className="mb-1 text-sm font-semibold">{t('analysis.progressBy', { dim: dims.label(main) })}</h2>
          <GroupProgress groups={groups} progressBy={data.config.progressBy} head={dims.label(main)} valueLabel={(v) => dims.valueLabel(main, v)} />
        </section>
      )}
      {parts.has('overview') && phaseGroups.length > 0 && (
        <section className="print-avoid-break">
          <h2 className="mb-1 text-sm font-semibold">{t('analysis.progressBy', { dim: dims.label('phase') })}</h2>
          <GroupProgress groups={phaseGroups} progressBy={data.config.progressBy} head={dims.label('phase')} valueLabel={(v) => dims.valueLabel('phase', v)} showHours={false} />
        </section>
      )}
      {parts.has('matrix') && matrix && (
        <section className="print-avoid-break print-break-before">
          <h2 className="mb-1 text-sm font-semibold">
            {dims.label('floor')} × {dims.label('trade')}
          </h2>
          <MatrixTable model={matrix} progressBy={data.config.progressBy} rowLabel={dims.label('floor')} colLabel={dims.label('trade')} valueLabel={(w, v) => dims.valueLabel(w === 'row' ? 'floor' : 'trade', v)} />
        </section>
      )}
      {parts.has('schedule') && (
        <>
          <section className="print-avoid-break">
            <h2 className="mb-1 text-sm font-semibold">{t('analysis.burn.title')}</h2>
            <BurnUp points={points} tasks={model.tasks} height={180} />
          </section>
          <section className="print-break-before">
            <h2 className="mb-1 text-sm font-semibold">{t('analysis.tabs.schedule')}</h2>
            <Gantt items={ganttItems(model.tasks, main, model.opts, 400)} today={data.today} groupLabel={(v) => dims.valueLabel(main, v || NONE)} formaLink={link} limit={400} />
          </section>
          <section className="print-avoid-break">
            <h2 className="mb-1 text-sm font-semibold">{t('analysis.overdueTop')}</h2>
            <OverdueList items={overdue} today={data.today} />
          </section>
        </>
      )}
      {parts.has('team') && (
        <section className="print-avoid-break">
          <h2 className="mb-1 text-sm font-semibold">{t('analysis.whoWhere')}</h2>
          <PersonHours pm={pm} colLabel={dims.label(main)} valueLabel={(v) => dims.valueLabel(main, v)} personName={dims.personName} />
        </section>
      )}
      {parts.has('tasks') && <ReportTasks data={data} model={model} />}
      {parts.has('cost') && (data.money.cost || data.money.sales) && <ReportCost data={data} model={model} dims={dims} dim={main} />}
      <StageLegend />
      <p className="text-[10px] text-graphite">{t('analysis.definitions')}</p>
    </div>
  );
}

/** All tasks of the report (filtered like the screen) with status, dates, plan and hours. */
function ReportTasks({ data, model }: { data: AnalysisData; model: Model }) {
  const { t } = useTranslation();
  const cfg = data.config;
  const lifetime = new Map<string, number>();
  for (const x of model.hours) lifetime.set(x.taskId, (lifetime.get(x.taskId) ?? 0) + x.minutes);
  const rows = model.tasks.filter((x) => !x.archived).sort((a, b) => a.ref.localeCompare(b.ref, 'de', { numeric: true }));
  return (
    <section className="print-break-before">
      <h2 className="mb-1 text-sm font-semibold">{t('analysis.tabs.tasks')}</h2>
      <table className="w-full text-xs">
        <thead className="border-b border-line">
          <tr>
            <th className={th}>{t('common.task')}</th>
            {cfg.building.kind !== 'none' && <th className={th}>{t('analysis.dims.building')}</th>}
            {cfg.floor.kind !== 'none' && <th className={th}>{t('analysis.dims.floor')}</th>}
            {cfg.trade && <th className={th}>{t('analysis.dims.trade')}</th>}
            <th className={th}>{t('common.status')}</th>
            <th className={th}>{t('analysis.startDue')}</th>
            <th className={clsx(th, 'text-right')}>{t('analysis.kpi.plan')}</th>
            <th className={clsx(th, 'text-right')}>{t('analysis.kpi.lifetimeShort')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((x) => {
            const late = x.statusGroup === 'active' && x.dueDate && x.dueDate < data.today;
            const actual = lifetime.get(x.id) ?? 0;
            return (
              <tr key={x.id} className="border-b border-line/60 print-avoid-break">
                <td className={td}>
                  <span className="num font-semibold">{x.ref}</span> {x.title}
                </td>
                {cfg.building.kind !== 'none' && <td className={td}>{taskDimValue(x, 'building', cfg)}</td>}
                {cfg.floor.kind !== 'none' && <td className={td}>{taskDimValue(x, 'floor', cfg)}</td>}
                {cfg.trade && <td className={td}>{taskDimValue(x, 'trade', cfg)}</td>}
                <td className={clsx(td, 'whitespace-nowrap')}>{t(`task.status.${x.status as 'open'}`, { defaultValue: x.status })}</td>
                <td className={clsx(td, 'num whitespace-nowrap', late && 'font-semibold text-bad')}>
                  {x.startDate ? d(x.startDate) : '-'} - {x.dueDate ? d(x.dueDate) : '-'}
                </td>
                <td className={clsx(td, 'num text-right')}>{x.plannedMinutes ? h(x.plannedMinutes, 1) : '-'}</td>
                <td className={clsx(td, 'num text-right')}>{actual ? h(actual, 1) : '-'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

/** Costs and sales value by the main dimension (only with the rate rights; the screen shows the same). */
function ReportCost({ data, model, dims, dim }: { data: AnalysisData; model: Model; dims: Dims; dim: string }) {
  const { t } = useTranslation();
  const groups = groupBy(model.tasks, model.hours, dim, model.opts, true).sort((a, b) => (b.costCents ?? 0) - (a.costCents ?? 0));
  const total = groupStat('all', '', model.tasks, model.hours, model.opts, true);
  return (
    <section className="print-avoid-break">
      <h2 className="mb-1 text-sm font-semibold">{t('analysis.tabs.cost')}</h2>
      <table className="w-full text-xs">
        <thead className="border-b border-line">
          <tr>
            <th className={th}>{dims.label(dim)}</th>
            {data.money.cost && <th className={clsx(th, 'text-right')}>{t('analysis.kpi.cost')}</th>}
            {data.money.sales && <th className={clsx(th, 'text-right')}>{t('analysis.kpi.sales')}</th>}
            <th className={clsx(th, 'text-right')}>{t('analysis.kpi.actual')}</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => (
            <tr key={g.key} className="border-b border-line/60">
              <td className={td}>{dims.valueLabel(dim, g.key)}</td>
              {data.money.cost && <td className={clsx(td, 'num text-right')}>{money(g.costCents)}</td>}
              {data.money.sales && <td className={clsx(td, 'num text-right')}>{money(g.salesCents)}</td>}
              <td className={clsx(td, 'num text-right')}>{h(g.actualMinutes, 1)} h</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="font-semibold">
            <td className={td}>{t('common.total')}</td>
            {data.money.cost && <td className={clsx(td, 'num text-right')}>{money(total.costCents)}</td>}
            {data.money.sales && <td className={clsx(td, 'num text-right')}>{money(total.salesCents)}</td>}
            <td className={clsx(td, 'num text-right')}>{h(total.actualMinutes, 1)} h</td>
          </tr>
        </tfoot>
      </table>
      <p className="mt-1 text-[10px] text-graphite">{t('analysis.costNote')}</p>
    </section>
  );
}
